import { useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link, useParams } from "@tanstack/react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { LuArrowLeft, LuBan, LuCalendarClock, LuPencil } from "react-icons/lu";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Field,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { DetailSkeleton } from "../components/LoadingSkeletons";
import { PageHeader } from "../components/PageHeader";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";
import { useEffectivePersonId } from "../impersonation";
import { useViewerAccess } from "@/lib/access";
import { eventKindLabel } from "./eventsShared";
import { EventRoster } from "./EventRoster";
import { EventFeedbackPanel } from "./EventFeedbackPanel";

export function EventDetailPage() {
  const { eventId } = useParams({ from: "/_authed/events/$eventId" });
  const effectivePersonId = useEffectivePersonId();

  const event = useQuery(api.events.getEvent, {
    eventId: eventId as Id<"events">,
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const access = useViewerAccess();
  const isAdmin = access?.isActivityAdmin ?? false;

  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const breadcrumbs = [
    { label: "CeMMbase", to: "/" },
    { label: "Events", to: "/events" },
    { label: event?.title ?? "Event" },
  ];

  if (event === undefined) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Event" description="" loading />
        <Box px={{ base: 6, sm: 8, lg: 10 }} py={6}>
          <DetailSkeleton label="Loading the event" sections={2} />
        </Box>
      </Box>
    );
  }

  if (event === null) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Not found" description="" />
        <Box px={{ base: 6, sm: 8, lg: 10 }} py={6}>
          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body p={6}>
              <Stack gap={3} align="start">
                <Text fontSize="sm" color="fg.muted">That event does not exist, or has been removed.</Text>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/events"><LuArrowLeft />Back to events</Link>
                </Button>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Box>
      </Box>
    );
  }

  const cancelled = event.status === "cancelled";

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        eyebrow="Events"
        title={event.title}
        description={event.description}
        actions={isAdmin && !cancelled ? (
          <HStack gap={2}>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}><LuPencil />Edit</Button>
            <Button size="sm" variant="outline" onClick={() => setMoving(true)}><LuCalendarClock />Move</Button>
            <Button size="sm" variant="outline" colorPalette="red" onClick={() => setCancelling(true)}>
              <LuBan />Cancel
            </Button>
          </HStack>
        ) : undefined}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={6}>
        <Stack gap={5} maxW="5xl">
          {cancelled ? (
            <Alert.Root status="error" variant="subtle">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>This event is cancelled</Alert.Title>
                <Alert.Description>
                  Everyone who was down for it has been told, and anything that was for it has been
                  cancelled with it.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}

          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body p={5}>
              <Stack gap={3}>
                <HStack gap={2} wrap="wrap">
                  <Badge variant="subtle">{eventKindLabel(event.kind)}</Badge>
                  {event.series === null ? null : (
                    <Badge variant="outline" colorPalette="purple">{event.series.name}</Badge>
                  )}
                </HStack>
                <Detail label="When">
                  {event.startDay}
                  {event.endDay === null || event.endDay === event.startDay ? "" : ` – ${event.endDay}`}
                  {event.startTime === null ? "" : ` · ${event.startTime}`}
                  {event.endTime === null ? "" : `–${event.endTime}`}
                </Detail>
                {event.location === "" ? null : <Detail label="Where">{event.location}</Detail>}
                <Detail label="Places">
                  {event.capacity === null
                    ? `No limit · ${event.taken} coming`
                    : `${event.taken} of ${event.capacity} taken${event.waiting > 0 ? ` · ${event.waiting} waiting` : ""}`}
                </Detail>
              </Stack>
            </Card.Body>
          </Card.Root>

          {cancelled ? null : (
            <SignUpCard event={event} effectivePersonId={effectivePersonId} />
          )}

          {isAdmin ? <EventRoster eventId={event._id} cancelled={cancelled} /> : null}
          {isAdmin ? <EventFeedbackPanel event={event} /> : null}
        </Stack>
      </Box>

      {isAdmin ? (
        <>
          <EditEventDialog event={event} open={editing} onClose={() => setEditing(false)} />
          <MoveEventDialog event={event} open={moving} onClose={() => setMoving(false)} />
          <CancelEventDialog event={event} open={cancelling} onClose={() => setCancelling(false)} />
        </>
      ) : null}
    </Box>
  );
}

type EventDoc = NonNullable<ReturnType<typeof useQuery<typeof api.events.getEvent>>>;

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <HStack gap={3} align="baseline">
      <Text minW="5rem" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.1em" color="fg.muted">
        {label}
      </Text>
      <Text fontSize="sm">{children}</Text>
    </HStack>
  );
}

function SignUpCard({ event, effectivePersonId }: { event: EventDoc; effectivePersonId: Id<"persons"> | null }) {
  const signUp = useMutation(api.participations.signUp);
  const withdraw = useMutation(api.participations.withdraw);
  const [busy, setBusy] = useState(false);

  const mine = event.mine;
  const holding = mine !== null && mine.state !== "cancelled";

  async function run(what: "signUp" | "withdraw") {
    setBusy(true);
    try {
      if (what === "signUp") {
        const result = await signUp({
          eventId: event._id,
          note: "",
          effectivePersonId: effectivePersonId as Id<"persons"> | null,
        });
        toastSuccess(
          result.waitlisted ? "You are on the waitlist" : "You are signed up",
          result.waitlisted
            ? "There was no place free. You will be told if one comes up."
            : event.title,
        );
      } else {
        if (mine === null) {
          return;
        }
        const result = await withdraw({
          participationId: mine.participationId,
          effectivePersonId: effectivePersonId as Id<"persons"> | null,
        });
        toastSuccess(
          "You have withdrawn",
          result.promoted === null ? event.title : "Somebody on the waitlist has taken your place.",
        );
      }
    } catch (error) {
      const { message, detail } = readableError(
        error,
        what === "signUp" ? "You could not be signed up" : "You could not be withdrawn",
      );
      toastError(message, detail);
    } finally {
      setBusy(false);
    }
  }

  if (!event.signUpOpen && !holding) {
    return (
      <Card.Root borderWidth="1px" borderColor="border">
        <Card.Body p={5}>
          <Text fontSize="sm" color="fg.muted">Sign-up is not open for this event.</Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={5}>
        <Stack gap={3} align="start">
          {mine === null || mine.state === "cancelled" ? (
            <>
              <Text fontSize="sm" color="fg.muted">
                {event.capacity !== null && event.taken >= event.capacity
                  ? event.waitlistEnabled
                    ? "This event is full. Signing up puts you on the waitlist, and you will be told if a place comes free."
                    : "This event is full."
                  : "Attendance is confirmed afterwards by whoever runs the event."}
              </Text>
              <Button
                colorPalette="blue"
                loading={busy}
                disabled={event.capacity !== null && event.taken >= event.capacity && !event.waitlistEnabled}
                onClick={() => void run("signUp")}
              >
                Sign up
              </Button>
            </>
          ) : (
            <>
              <HStack gap={2}>
                <Badge colorPalette={mine.state === "waitlisted" ? "orange" : "green"}>
                  {mine.state === "waitlisted"
                    ? `Waitlisted — number ${mine.waitlistPosition ?? "?"} in the queue`
                    : mine.state === "confirmed"
                      ? "You took part"
                      : "You are going"}
                </Badge>
              </HStack>
              {mine.state === "confirmed" ? (
                <Text fontSize="sm" color="fg.muted">
                  Your attendance has been confirmed.
                </Text>
              ) : (
                <Button variant="outline" loading={busy} onClick={() => void run("withdraw")}>
                  Withdraw
                </Button>
              )}
            </>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

function EditEventDialog({ event, open, onClose }: { event: EventDoc; open: boolean; onClose: () => void }) {
  const updateEvent = useMutation(api.events.updateEvent);
  const [draft, setDraft] = useState({
    title: event.title,
    description: event.description,
    kind: event.kind as string,
    location: event.location,
    capacity: event.capacity === null ? "" : String(event.capacity),
    signUpOpen: event.signUpOpen,
    waitlistEnabled: event.waitlistEnabled,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateEvent({
        eventId: event._id,
        title: draft.title,
        description: draft.description,
        kind: draft.kind as never,
        location: draft.location,
        capacity: draft.capacity === "" ? null : Number(draft.capacity),
        signUpOpen: draft.signUpOpen,
        waitlistEnabled: draft.waitlistEnabled,
      });
      toastSuccess("Event updated", draft.title);
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The event could not be updated");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Edit event</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  Dates are changed with Move, which also carries the people and the work
                  attached to this occasion.
                </Text>
                <Field.Root required>
                  <Field.Label>Title</Field.Label>
                  <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Description</Field.Label>
                  <Textarea
                    rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  />
                </Field.Root>
                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>Kind</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        value={draft.kind}
                        onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                      >
                        <option value="seminar">Seminar</option>
                        <option value="workshop">Workshop</option>
                        <option value="instituteDay">Institute day</option>
                        <option value="other">Other</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Places</Field.Label>
                    <Input
                      type="number"
                      min={1}
                      value={draft.capacity}
                      onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
                <Field.Root>
                  <Field.Label>Location</Field.Label>
                  <Input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
                </Field.Root>
                <HStack gap={6}>
                  <Field.Root orientation="horizontal">
                    <Field.Label>Sign-up open</Field.Label>
                    <input
                      type="checkbox"
                      checked={draft.signUpOpen}
                      onChange={(e) => setDraft({ ...draft, signUpOpen: e.target.checked })}
                    />
                  </Field.Root>
                  <Field.Root orientation="horizontal">
                    <Field.Label>Keep a waitlist</Field.Label>
                    <input
                      type="checkbox"
                      checked={draft.waitlistEnabled}
                      onChange={(e) => setDraft({ ...draft, waitlistEnabled: e.target.checked })}
                    />
                  </Field.Root>
                </HStack>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button colorPalette="blue" loading={saving} onClick={() => void save()}>Save</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function MoveEventDialog({ event, open, onClose }: { event: EventDoc; open: boolean; onClose: () => void }) {
  const moveEvent = useMutation(api.events.moveEvent);
  const [draft, setDraft] = useState({
    startDay: event.startDay,
    endDay: event.endDay ?? "",
    startTime: event.startTime ?? "",
    endTime: event.endTime ?? "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const result = await moveEvent({
        eventId: event._id,
        startDay: draft.startDay,
        endDay: draft.endDay === "" ? null : draft.endDay,
        startTime: draft.startTime === "" ? null : draft.startTime,
        endTime: draft.endTime === "" ? null : draft.endTime,
        reason: draft.reason,
      });
      toastSuccess(
        "Event moved",
        `${result.restated} on the list told${result.moved > 0 ? `, ${result.moved} assigned activities moved with it` : ""}.`,
      );
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The event could not be moved");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Move {event.title}</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  Everyone on the list is told and their dates follow. Anyone already marked as having
                  taken part drops back to coming, since the occasion has not happened yet. Activities
                  anchored to this event move by the same number of days; no answers or files are lost.
                </Text>
                <HStack gap={3}>
                  <Field.Root required>
                    <Field.Label>New start</Field.Label>
                    <Input
                      type="date"
                      value={draft.startDay}
                      onChange={(e) => setDraft({ ...draft, startDay: e.target.value })}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>New end</Field.Label>
                    <Input
                      type="date"
                      value={draft.endDay}
                      onChange={(e) => setDraft({ ...draft, endDay: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>From</Field.Label>
                    <Input
                      type="time"
                      value={draft.startTime}
                      onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>To</Field.Label>
                    <Input
                      type="time"
                      value={draft.endTime}
                      onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
                <Field.Root required>
                  <Field.Label>Why</Field.Label>
                  <Textarea
                    rows={2}
                    value={draft.reason}
                    onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                  />
                  <Field.HelperText>This is what everybody affected is told.</Field.HelperText>
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="blue"
                loading={saving}
                disabled={draft.reason.trim() === "" || draft.startDay === ""}
                onClick={() => void save()}
              >
                Move it
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function CancelEventDialog({ event, open, onClose }: { event: EventDoc; open: boolean; onClose: () => void }) {
  const cancelEvent = useMutation(api.events.cancelEvent);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const result = await cancelEvent({ eventId: event._id, reason });
      toastSuccess(
        "Event cancelled",
        `${result.withdrawn} on the list told${result.cancelled > 0 ? `, ${result.cancelled} assigned activities cancelled with it` : ""}.`,
      );
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The event could not be cancelled");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} role="alertdialog">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Cancel {event.title}?</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  The event is kept and marked cancelled rather than deleted — people planned around
                  it. Everyone on the list is told and withdrawn, and any activity that existed for
                  this occasion is cancelled with it. This cannot be undone from here.
                </Text>
                <Field.Root required>
                  <Field.Label>Why</Field.Label>
                  <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Field.HelperText>This is what everybody affected is told.</Field.HelperText>
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Keep it</Button>
              <Button
                colorPalette="red"
                loading={saving}
                disabled={reason.trim() === ""}
                onClick={() => void save()}
              >
                Cancel the event
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
